import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MFAVerification } from "@/components/MFAVerification";
import { useAuth } from "@/hooks/useAuth";

import { useToast } from "@/hooks/use-toast";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";
import {
  trackLoginComplete,
  trackFunnelSigninView,
  trackFunnelOAuthStart,
  trackFunnelOAuthError,
} from "@/lib/analytics";
import { lovable } from "@/integrations/lovable";
import logoAtlas from "@/assets/logo-atlas.png";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { z } from "zod";
import { TrialConfirmationBanner } from "@/components/auth/TrialConfirmationBanner";
import { setPostOAuthRedirect } from "@/lib/oauthRedirect";

interface AuthPageProps {}

const AuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, signIn, signUp, resetPassword, updatePassword, getAuthenticatorAssuranceLevel, listMFAFactors } = useAuth();
  const { toast } = useToast();
  useSEO({
    title: "Sign In | System Atlas - AI Study Assistant",
    description: "Log in to System Atlas. Access your revision sheets, quizzes, and study plan. Continue studying smarter with AI.",
  });
  
  const emailSchema = z.string().email(t("settings.invalidEmail"));
  const passwordSchema = z.string().min(6, t("settings.passwordMinLength"));
  const displayNameSchema = z.string().min(2, t("authPage.displayNamePlaceholder"));
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupDisplayName, setSignupDisplayName] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  const authSource = searchParams.get("source") || "direct";
  const tabParam = searchParams.get("tab");
  const preferredTab = useMemo(() => {
    if (tabParam === "signup" || tabParam === "login") return tabParam;
    if (["input_bar_cta", "trial_badge", "trial_modal", "pricing_page"].includes(authSource)) {
      return "signup";
    }
    return "login";
  }, [authSource, tabParam]);

  const showTrialReassurance = authSource !== "direct" || tabParam === "signup";

  // Single source of truth for the AuthPage shell.
  // - `h-[100svh]` locks the page to the small-viewport height so the mobile
  //   URL bar / keyboard don't introduce extra scroll on iOS/Android.
  // - `overflow-hidden` prevents any internal element from triggering a
  //   page-level scrollbar at typical screen sizes.
  // - The `@media (max-height: 640px)` arbitrary variant flips overflow back
  //   to `auto` on very short viewports (e.g. iPhone SE landscape) so the
  //   form remains reachable instead of being clipped.
  // - `overscroll-contain` blocks bounce scrolling on iOS Safari.
  const SHELL_CLASSES =
    "h-[100svh] w-full flex flex-col items-center justify-center px-4 py-4 sm:py-6 overflow-hidden overscroll-contain [@media(max-height:640px)]:overflow-y-auto [@media(max-height:640px)]:justify-start [@media(max-height:640px)]:py-6";

  // Email form is hidden by default to match the simplified mockup; users
  // expand it via the discrete "Or continue with email" link.
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    if (searchParams.get("reset") === "true" && user) {
      setShowNewPassword(true);
    } else if (user && !loading && !showMFAVerification) {
      checkMFAStatus();
    }
  }, [user, loading, navigate, searchParams]);

  // Funnel step 4: sign-in page viewed (track once per mount, only when guest)
  useEffect(() => {
    if (user) return;
    trackFunnelSigninView(authSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkMFAStatus = async () => {
    const { data: aalData } = await getAuthenticatorAssuranceLevel();
    if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
      const { data: factorsData } = await listMFAFactors();
      if (factorsData && factorsData.totp.length > 0) {
        const verifiedFactor = factorsData.totp.find(f => f.status === 'verified');
        if (verifiedFactor) {
          setMfaFactorId(verifiedFactor.id);
          setShowMFAVerification(true);
          return;
        }
      }
    }
    if (user) {
      navigate("/");
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    if (preferredTab === "signup") {
      sessionStorage.setItem("pending_signup_source", authSource);
    }
    sessionStorage.setItem("pending_oauth_provider", "google");
    sessionStorage.setItem("pending_oauth_source", authSource);
    trackFunnelOAuthStart("google", authSource);
    // Honor an optional ?next= path so callers (e.g. guest sheet "Sign up"
    // links) can route the user back to the exact preview state.
    const next = searchParams.get("next");
    if (next && next.startsWith("/") && !next.startsWith("/sign-in")) {
      try {
        localStorage.setItem(
          "atlas_post_oauth_redirect",
          JSON.stringify({ path: next, ts: Date.now(), source: authSource })
        );
      } catch { /* ignore */ }
    } else {
      setPostOAuthRedirect(authSource);
    }
    const { error } = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
    if (error) {
      sessionStorage.removeItem("pending_signup_source");
      sessionStorage.removeItem("pending_oauth_provider");
      sessionStorage.removeItem("pending_oauth_source");
      trackFunnelOAuthError("google", authSource, error.message || "unknown");
      toast({ title: t("authPage.signInFailed"), description: error.message || t("authPage.incorrectCredentials"), variant: "destructive" });
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    if (preferredTab === "signup") {
      sessionStorage.setItem("pending_signup_source", authSource);
    }
    sessionStorage.setItem("pending_oauth_provider", "apple");
    sessionStorage.setItem("pending_oauth_source", authSource);
    trackFunnelOAuthStart("apple", authSource);
    const next = searchParams.get("next");
    if (next && next.startsWith("/") && !next.startsWith("/sign-in")) {
      try {
        localStorage.setItem(
          "atlas_post_oauth_redirect",
          JSON.stringify({ path: next, ts: Date.now(), source: authSource })
        );
      } catch { /* ignore */ }
    } else {
      setPostOAuthRedirect(authSource);
    }
    const { error } = await lovable.auth.signInWithOAuth("apple", { redirect_uri: window.location.origin });
    if (error) {
      sessionStorage.removeItem("pending_signup_source");
      sessionStorage.removeItem("pending_oauth_provider");
      sessionStorage.removeItem("pending_oauth_source");
      trackFunnelOAuthError("apple", authSource, error.message || "unknown");
      toast({ title: t("authPage.signInFailed"), description: error.message || t("authPage.incorrectCredentials"), variant: "destructive" });
      setIsAppleLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      emailSchema.parse(loginEmail);
      passwordSchema.parse(loginPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: t("authPage.validationError"), description: err.errors[0]?.message ?? "Invalid input", variant: "destructive" });
        return;
      }
    }
    setIsSubmitting(true);
    const { error } = await signIn(loginEmail, loginPassword);
    setIsSubmitting(false);
    if (error) {
      toast({ title: t("authPage.signInFailed"), description: error.message === "Invalid login credentials" ? t("authPage.incorrectCredentials") : error.message, variant: "destructive" });
    } else {
      trackLoginComplete();
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      emailSchema.parse(signupEmail);
      passwordSchema.parse(signupPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: t("authPage.validationError"), description: err.errors[0]?.message ?? "Invalid input", variant: "destructive" });
        return;
      }
    }
    setIsSubmitting(true);
    sessionStorage.setItem("pending_signup_source", authSource);
    // Derive a sensible default display name from the email's local-part so
    // the signup form only asks for the strict minimum (email + password).
    // Users can rename themselves later from their profile settings.
    const derivedDisplayName =
      signupDisplayName.trim() ||
      (signupEmail.split("@")[0] || "User").slice(0, 40);
    const { error } = await signUp(signupEmail, signupPassword, derivedDisplayName);
    setIsSubmitting(false);
    if (error) {
      sessionStorage.removeItem("pending_signup_source");
      if (error.message.includes("already registered")) {
        toast({ title: t("authPage.accountExists"), description: t("authPage.alreadyRegistered"), variant: "destructive" });
      } else {
        toast({ title: t("authPage.signUpFailed"), description: error.message, variant: "destructive" });
      }
    } else {
      toast({ title: t("authPage.accountCreated"), description: t("authPage.welcomeToSystemAtlas") });
      navigate("/");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      emailSchema.parse(forgotEmail);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: t("authPage.validationError"), description: err.errors[0]?.message ?? "Invalid input", variant: "destructive" });
        return;
      }
    }
    setIsSubmitting(true);
    const { error } = await resetPassword(forgotEmail);
    setIsSubmitting(false);
    if (error) {
      toast({ title: t("authPage.error"), description: error.message, variant: "destructive" });
    } else {
      setResetEmailSent(true);
      toast({ title: t("authPage.emailSent"), description: t("authPage.checkInboxForResetLink") });
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      passwordSchema.parse(newPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: t("authPage.validationError"), description: err.errors[0]?.message ?? "Invalid input", variant: "destructive" });
        return;
      }
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t("authPage.passwordsDontMatch"), description: t("authPage.passwordsMustMatch"), variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    const { error } = await updatePassword(newPassword);
    setIsSubmitting(false);
    if (error) {
      toast({ title: t("authPage.error"), description: error.message, variant: "destructive" });
    } else {
      toast({ title: t("authPage.passwordUpdated"), description: t("authPage.passwordChangedSuccessfully") });
      setShowNewPassword(false);
      navigate("/");
    }
  };


  if (loading) {
    return (
      <>
        <div className={SHELL_CLASSES}>
          <h1 className="sr-only">{t("authPage.signIn")}</h1>
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (showMFAVerification && mfaFactorId) {
    return (
      <>
        <div className={SHELL_CLASSES}>
          <h1 className="sr-only">{t("authPage.signIn")}</h1>
          <MFAVerification
            factorId={mfaFactorId}
            onSuccess={() => { toast({ title: t("authPage.welcomeBack"), description: t("authPage.signedInSuccessfully") }); navigate("/"); }}
            onCancel={() => { setShowMFAVerification(false); setMfaFactorId(null); }}
          />
        </div>
      </>
    );
  }

  if (showNewPassword) {
    return (
      <>
        <div className={SHELL_CLASSES}>
          <h1 className="sr-only">{t("authPage.signIn")}</h1>
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="font-serif text-2xl">{t("authPage.newPasswordTitle")}</CardTitle>
              <CardDescription>{t("authPage.enterNewPasswordBelow")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t("authPage.newPassword")}</Label>
                  <Input id="new-password" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t("authPage.confirmPassword")}</Label>
                  <Input id="confirm-password" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("authPage.updating")}</>) : t("authPage.updatePassword")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (showForgotPassword) {
    return (
      <>
        <div className={SHELL_CLASSES}>
          <h1 className="sr-only">{t("authPage.signIn")}</h1>
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="font-serif text-2xl">{t("authPage.resetPassword")}</CardTitle>
              <CardDescription>
                {resetEmailSent ? t("authPage.checkEmailForResetLink") : t("authPage.enterEmailForResetLink")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetEmailSent ? (
                <div className="space-y-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("authPage.resetLinkSent")} <strong>{forgotEmail}</strong>. {t("authPage.clickLinkInEmail")}
                  </p>
                  <Button variant="outline" onClick={() => { setShowForgotPassword(false); setResetEmailSent(false); setForgotEmail(""); }} className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />{t("authPage.backToSignIn")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">{t("auth.email")}</Label>
                    <Input id="forgot-email" type="email" placeholder="you@example.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("authPage.sending")}</>) : t("authPage.sendResetLink")}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForgotPassword(false)} className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />{t("authPage.backToSignIn")}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={SHELL_CLASSES}>
          <h1 className="sr-only">{t("authPage.signIn")}</h1>
        <TrialConfirmationBanner />

        <Card className="w-full max-w-md">
          <CardHeader className="text-center pb-3 space-y-2">
            <div className="flex justify-center">
              <img src={logoAtlas} alt="System Atlas" className="w-8 h-8" />
            </div>
            <CardTitle className="font-serif text-2xl">{t("authPage.signIn")}</CardTitle>
            {showTrialReassurance && (
              <p className="text-xs text-muted-foreground">
                {t("offerDetails.freeNote")}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {/* OAuth Buttons */}
            <div className="space-y-2">
              <Button variant="outline" className="w-full gap-3 h-10" onClick={handleGoogleSignIn} disabled={isGoogleLoading || isAppleLoading}>
                {isGoogleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                {t("authPage.continueWithGoogle")}
              </Button>

              <Button variant="outline" className="w-full gap-3 h-10" onClick={handleAppleSignIn} disabled={isGoogleLoading || isAppleLoading}>
                {isAppleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                )}
                {t("authPage.continueWithApple", "Continue with Apple")}
              </Button>
            </div>

            {!showEmailForm ? (
              <div className="flex justify-center pt-0.5">
                <button
                  type="button"
                  onClick={() => setShowEmailForm(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {t("authPage.orContinueWithEmail", "Or continue with email")}
                </button>
              </div>
            ) : (
            <Tabs defaultValue={preferredTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">{t("authPage.signIn")}</TabsTrigger>
                <TabsTrigger value="signup">{t("authPage.signUp")}</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t("auth.email")}</Label>
                    <Input id="login-email" type="email" placeholder="you@example.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t("auth.password")}</Label>
                    <Input id="login-password" type="password" placeholder="••••••••" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full h-10" disabled={isSubmitting}>
                    {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("authPage.signingIn")}</>) : t("authPage.signIn")}
                  </Button>
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {t("authPage.forgotYourPassword")}
                  </button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{t("auth.email")}</Label>
                    <Input id="signup-email" type="email" placeholder="you@example.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{t("auth.password")}</Label>
                    <Input id="signup-password" type="password" placeholder="••••••••" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full h-10" disabled={isSubmitting}>
                    {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("authPage.creatingAccount")}</>) : t("authPage.createAccount")}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AuthPage;
